import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { TradingEngine } from "../trading-engine";
import { useEffect, useMemo } from "react";
import { useBackendAccount } from "@/backend/hooks/use-backend-account";
import { debug } from "@/lib/debug";

export const useAccountSummary = () => {
  const { userId, isAuthenticated } = useUnifiedAuth();
  
  // Determine effective user ID (works for both wallet and email users)
  const effectiveUserId = userId;
  const isEffectivelyConnected = isAuthenticated;

  // Initialize user account when wallet connects (for TradingEngine side effects)
  useEffect(() => {
    if (effectiveUserId) {
      TradingEngine.initializeUserAccount(effectiveUserId)
        .then(() => {
          debug.log('useAccountSummary', `Account initialized for ${effectiveUserId.slice(0, 8)}...`);
        })
        .catch((err) => {
          debug.error('useAccountSummary', 'Failed to initialize account:', err);
        });
    }
  }, [effectiveUserId]);

  // Account sync is now handled at the app level in BackendEventsSubscriber
  // Just read the data from the store via useBackendAccount
  const backendUserId = effectiveUserId ?? undefined;

  // Use centralized hook with store-backed data
  const { summary: backendSummary, isLoading, refetch } = useBackendAccount(backendUserId);
  
  // Transform to expected format
  const summary = useMemo(() => {
    if (!backendSummary) return null;
    return {
      collateral: backendSummary.collateral,
      availableBalance: backendSummary.availableBalance,
      usedMargin: backendSummary.usedMargin,
      totalPnl: backendSummary.totalPnl,
      unrealizedPnl: backendSummary.unrealizedPnl,
      marginRatio: backendSummary.marginRatio,
      accountLeverage: backendSummary.accountLeverage,
    };
  }, [backendSummary]);

  return {
    summary,
    isLoading,
    connected: isEffectivelyConnected,
    refetch,
  };
};
